import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, HandCoins, AlertTriangle, RotateCcw, UserCog, Download, FileText } from "lucide-react";
import { printDoc } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  cash_removal: { label: "Cash Removal", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30", Icon: HandCoins },
  shift_short: { label: "Shift Shortage", cls: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30", Icon: AlertTriangle },
  refund: { label: "Refund", cls: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30", Icon: RotateCcw },
  credit_change: { label: "Credit Change", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30", Icon: UserCog },
};

export default function ApprovalsReport() {
  const { business } = useAuth();
  const cur = business?.currency || "JMD $";
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });

  const load = () => api.get("/approvals", { params: { kind, start: range.start, end: range.end } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [kind, range]);

  const totals = useMemo(() => {
    const t = { cash_removal: 0, shift_short: 0, refund: 0 };
    rows.forEach((r) => { t[r.type] = (t[r.type] || 0) + (r.amount || 0); });
    return t;
  }, [rows]);

  const fmtWhen = (at) => (at ? at.slice(0, 16).replace("T", " ") : "");
  const amtCell = (r) => (r.type === "credit_change" ? "-" : money(r.amount, cur));

  const exportCsv = () => {
    const head = ["Date/Time", "Type", "Amount", "Requested By", "Reason", "Approved By", "Reference"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(",")];
    rows.forEach((r) => lines.push([fmtWhen(r.at), r.type_label, r.type === "credit_change" ? "" : (r.amount || 0),
      r.requester || "", r.reason || "", r.approved_by || "", r.reference || ""].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `approvals-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const rowsHtml = rows.map((r) => `<tr>
      <td>${fmtWhen(r.at)}</td><td>${r.type_label}</td>
      <td style="text-align:right">${r.type === "credit_change" ? "-" : money(r.amount, cur)}</td>
      <td>${(r.requester || "-")}</td><td>${(r.reason || "")}</td>
      <td>${(r.approved_by || "-")}</td><td>${(r.reference || "")}</td></tr>`).join("");
    const body = `<table style="width:100%;border-collapse:collapse;font-size:12px">
      <thead><tr style="background:#f1f5f9;text-align:left">
      <th style="padding:6px">When</th><th style="padding:6px">Type</th><th style="padding:6px;text-align:right">Amount</th>
      <th style="padding:6px">Requested By</th><th style="padding:6px">Reason</th><th style="padding:6px">Approved By</th><th style="padding:6px">Reference</th>
      </tr></thead><tbody>${rowsHtml || '<tr><td colspan="7" style="padding:12px;text-align:center;color:#64748b">No records</td></tr>'}</tbody></table>`;
    printDoc({ business, docTitle: "Approvals Report", docRef: new Date().toLocaleDateString(),
      meta: [["Type", kind === "all" ? "All" : (TYPE_META[kind]?.label || kind)], ["From", range.start || "start"], ["To", range.end || "today"], ["Records", String(rows.length)]],
      body, accent: "#8b5cf6" });
  };

  return (
    <div className="space-y-6" data-testid="approvals-report">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-violet-500" /> Approvals Report</h1>
        <p className="text-muted-foreground">A read-only register of every approval-controlled transaction (cash removals, shift shortages and refunds), with who requested and who approved.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><HandCoins className="w-3.5 h-3.5" /> Cash Removals</p><p className="mono text-xl font-bold text-amber-500">{money(totals.cash_removal)}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Shift Shortages</p><p className="mono text-xl font-bold text-rose-500">{money(totals.shift_short)}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Refunds</p><p className="mono text-xl font-bold text-violet-500">{money(totals.refund)}</p></Card>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Type</Label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-44 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="ap-type-filter">
            <option value="all">All types</option>
            <option value="cash_removal">Cash Removals</option>
            <option value="shift_short">Shift Shortages</option>
            <option value="refund">Refunds</option>
            <option value="credit_change">Credit Changes</option>
          </select>
        </div>
        <div><Label className="text-xs">From</Label><DatePicker value={range.start} onChange={(v) => setRange({ ...range, start: v })} data-testid="ap-start" /></div>
        <div><Label className="text-xs">To</Label><DatePicker value={range.end} onChange={(v) => setRange({ ...range, end: v })} data-testid="ap-end" /></div>
        {(kind !== "all" || range.start || range.end) && <Button variant="ghost" onClick={() => { setKind("all"); setRange({ start: "", end: "" }); }} data-testid="ap-clear">Clear</Button>}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="ap-export-csv"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
          <Button variant="outline" onClick={exportPdf} data-testid="ap-export-pdf"><FileText className="w-4 h-4 mr-1.5" /> PDF</Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date / Time</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead>
            <TableHead>Requested By</TableHead><TableHead>Reason</TableHead><TableHead>Approved By</TableHead><TableHead>Reference</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const meta = TYPE_META[r.type] || { label: r.type_label, cls: "bg-secondary text-muted-foreground border-border", Icon: ShieldCheck };
              const Icon = meta.Icon;
              return (
                <TableRow key={i} data-testid={`approval-row-${i}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.at)}<br /><span className="text-muted-foreground">{r.at ? new Date(r.at).toLocaleTimeString() : ""}</span></TableCell>
                  <TableCell><Badge className={meta.cls}><Icon className="w-3 h-3 mr-1" />{meta.label}</Badge></TableCell>
                  <TableCell className="text-right mono font-bold">{amtCell(r)}</TableCell>
                  <TableCell className="font-medium">{r.requester || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{r.reason || "-"}</TableCell>
                  <TableCell>{r.approved_by ? <span className="flex items-center gap-1 text-violet-500 text-sm"><ShieldCheck className="w-4 h-4" />{r.approved_by}</span> : <span className="text-muted-foreground text-sm">-</span>}</TableCell>
                  <TableCell className="text-xs mono text-muted-foreground">{r.reference || "-"}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No approval-controlled transactions in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
